import { FILM_BOOKEND_SECONDS, MAX_RENDER_DURATION_SECONDS, PLAYBACK_PACE_OPTIONS, playbackDurationSeconds,
  type PlaybackSecondsPerKilometer } from './playback-pace';
import { measurementUnits, type UnitSystem } from './measurement-units';
import { filmTraversalBounds } from './camera-plan';
import { MIN_RENDER_DURATION_SECONDS } from './playback-pace';

export const MIN_TARGET_DURATION_SECONDS = FILM_BOOKEND_SECONDS + 1;
export const DEFAULT_TARGET_DURATION_SECONDS = 90;
export const TARGET_DURATION_PRESETS = [30, 60, 90, 120, 140, 180, 240] as const;

export interface FilmTiming {
  readonly mode: 'pace' | 'duration';
  readonly pace: PlaybackSecondsPerKilometer;
  readonly targetSeconds: number;
}

export function isTargetDuration(seconds: number): boolean {
  return Number.isInteger(seconds) && seconds >= MIN_TARGET_DURATION_SECONDS
    && seconds <= MAX_RENDER_DURATION_SECONDS;
}
export function isFilmDuration(seconds: number): boolean {
  return Number.isInteger(seconds) && seconds >= MIN_RENDER_DURATION_SECONDS && seconds <= MAX_RENDER_DURATION_SECONDS;
}

/** Uses the requested total directly so rounding the displayed pace cannot lengthen an export. */
export function filmDuration(distanceKilometers: number, timing: FilmTiming): number {
  if (timing.mode === 'pace') return playbackDurationSeconds(distanceKilometers, timing.pace);
  if (!isFilmDuration(timing.targetSeconds)) throw new RangeError(`Video length must be ${MIN_RENDER_DURATION_SECONDS}–${MAX_RENDER_DURATION_SECONDS} whole seconds.`);
  return timing.targetSeconds;
}

/** Uses the camera traversal interval; a stationary route has no meaningful per-km pace. */
export function effectivePlaybackPace(distanceKilometers: number, durationSeconds: number): number | null {
  if (!Number.isFinite(distanceKilometers) || distanceKilometers < 0) throw new RangeError('Invalid route distance.');
  if (!isFilmDuration(durationSeconds)) throw new RangeError('Invalid film duration.');
  const bounds = filmTraversalBounds(durationSeconds);
  return distanceKilometers === 0 ? null : (bounds.endSeconds - bounds.startSeconds) / distanceKilometers;
}

/** Offers distinct film lengths with the actual pace after applying the duration ceiling. */
export function pacedFilmOptions(distanceKilometers: number) {
  const options = PLAYBACK_PACE_OPTIONS.map(pace => {
    const durationSeconds = playbackDurationSeconds(distanceKilometers, pace);
    return { pace, durationSeconds, effectivePace: effectivePlaybackPace(distanceKilometers, durationSeconds) };
  });
  return options.filter((option, index) => options.findIndex(candidate => candidate.durationSeconds === option.durationSeconds) === index);
}

export function effectivePaceLabel(pace: number | null, unitSystem: UnitSystem = 'metric'): string {
  if (pace === null) return 'Stationary route';
  const units = measurementUnits(unitSystem);
  const displayedPace = units.pace(pace);
  return `${displayedPace < 0.01 ? '<0.01' : Number(displayedPace.toFixed(2))} video ${units.paceUnit}`;
}
